import * as React from "react";
import { ChevronRight } from "lucide-react";
import { PopoverContent } from "./popover";
import { cn } from "@/engine/support/classNames";

/**
 * One look for every dropdown in the app.
 *
 * The room has four of them — invite, settings, account, interact — and they
 * are read as one control surface, so the panel, the rows and the headings
 * live here rather than being re-styled at each call site. A phone gets the
 * full width of the viewport; a desktop keeps the compact column.
 */

export const MenuPanel = React.forwardRef<
  React.ElementRef<typeof PopoverContent>,
  React.ComponentPropsWithoutRef<typeof PopoverContent>
>(({ className, align = "end", sideOffset = 10, ...props }, ref) => (
  <PopoverContent
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    collisionPadding={8}
    className={cn(
      "w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-80",
      "rounded-2xl border-border/60 bg-popover/90 p-2 shadow-xl backdrop-blur-xl",
      className,
    )}
    {...props}
  />
));
MenuPanel.displayName = "MenuPanel";

/** A small caps label that names a group of rows. */
export function MenuHeading({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "px-3 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
    >
      {children}
    </p>
  );
}

export function MenuSeparator({ className }: { className?: string }) {
  return <div className={cn("my-1.5 h-px bg-border/60", className)} />;
}

/** Shared geometry of every interactive line in a menu. */
export const menuRowClass =
  "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 min-h-11 sm:min-h-12 sm:py-3 text-sm sm:text-base font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

interface MenuRowProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  /** Secondary line under the label. */
  description?: React.ReactNode;
  /** Rendered at the end of the row: a switch, a badge, a chevron. */
  trailing?: React.ReactNode;
  /** Show the navigation chevron; ignored when `trailing` is given. */
  chevron?: boolean;
}

export const MenuRow = React.forwardRef<HTMLButtonElement, MenuRowProps>(
  (
    { icon, children, description, trailing, chevron, className, ...props },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      className={cn(menuRowClass, className)}
      {...props}
    >
      {icon ? (
        <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center text-muted-foreground">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate">{children}</span>
        {description ? (
          <span className="block truncate text-xs font-normal text-muted-foreground">
            {description}
          </span>
        ) : null}
      </span>
      {trailing ??
        (chevron ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : null)}
    </button>
  ),
);
MenuRow.displayName = "MenuRow";
